/**
 * player-crud.ts - Player CRUD Operations
 *
 * Handles creating, reading, updating, and deleting players in tournaments
 * including form management and UI state persistence.
 */

import type { Player } from '../../../types/api';
import { API_BASE_URL } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';
import { showConfirmDialog } from '../../utils/confirm-dialog';

/**
 * Player request data
 */
interface PlayerData {
  firstName: string;
  lastName?: string;
  rating?: number;
  ratingStd?: number;
  ratingRapid?: number;
  ratingBlitz?: number;
  title?: string;
  club?: string;
  federation?: string;
  fideId?: string;
  federationCode?: string;
  birthDate?: string;
  gender?: 'M' | 'F';
}

/**
 * Set up Enter key handlers for player form inputs
 * @param tournamentId - The tournament ID
 */
export function setupPlayerFormKeyHandlers(tournamentId: string): void {
  const inputIds = [
    `player-first-name-${tournamentId}`,
    `player-last-name-${tournamentId}`,
    `player-rating-${tournamentId}`,
    `player-title-${tournamentId}`,
    `player-federation-${tournamentId}`,
    `player-club-${tournamentId}`,
    `player-fide-id-${tournamentId}`,
    `player-federation-code-${tournamentId}`,
    `player-birth-date-${tournamentId}`,
  ];

  inputIds.forEach(inputId => {
    const input = document.getElementById(inputId);
    if (input) {
      input.addEventListener('keydown', (e: Event) => {
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key === 'Enter') {
          keyEvent.preventDefault();
          addPlayer(tournamentId);
        }
      });
    }
  });
}

/**
 * Add or update a player in a tournament
 * @param tournamentId - The tournament ID
 */
export async function addPlayer(tournamentId: string): Promise<void> {
  const firstNameInput = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
  const lastNameInput = document.getElementById(`player-last-name-${tournamentId}`) as HTMLInputElement | null;
  const ratingInput = document.getElementById(`player-rating-${tournamentId}`) as HTMLInputElement | null;
  const titleInput = document.getElementById(`player-title-${tournamentId}`) as HTMLInputElement | null;
  const federationInput = document.getElementById(`player-federation-${tournamentId}`) as HTMLInputElement | null;
  const clubInput = document.getElementById(`player-club-${tournamentId}`) as HTMLInputElement | null;
  const fideIdInput = document.getElementById(`player-fide-id-${tournamentId}`) as HTMLInputElement | null;
  const federationCodeInput = document.getElementById(`player-federation-code-${tournamentId}`) as HTMLInputElement | null;
  const birthDateInput = document.getElementById(`player-birth-date-${tournamentId}`) as HTMLInputElement | null;
  const genderSelect = document.getElementById(`player-gender-${tournamentId}`) as HTMLSelectElement | null;
  const addButton = document.getElementById(`add-player-${tournamentId}`) as HTMLButtonElement | null;

  if (!firstNameInput || !lastNameInput || !ratingInput || !addButton) return;

  const firstName = firstNameInput.value.trim();
  const lastName = lastNameInput.value.trim();

  if (!firstName) {
    alert(t('player.enterFirstName', 'Please enter a first name'));
    return;
  }

  const player: PlayerData = {
    firstName,
    lastName,
    rating: parseInt(ratingInput.value) || undefined,
    title: titleInput?.value.trim() || undefined,
    club: clubInput?.value.trim() || undefined,
    federation: (federationInput?.value.trim().toUpperCase() || addButton.dataset['federation'] || undefined),
    fideId: fideIdInput?.value.trim() || addButton.dataset['fideId'] || undefined,
    federationCode: federationCodeInput?.value.trim() || addButton.dataset['federationCode'] || undefined,
    birthDate: birthDateInput?.value || undefined,
    gender: (genderSelect?.value as 'M' | 'F') || undefined
  };

  // Check if we're in edit mode
  const editingPlayerId = addButton.dataset['editingPlayerId'];

  if (editingPlayerId) {
    // UPDATE player
    // Save UI state
    const playersListDiv = document.getElementById(`players-list-${tournamentId}`);
    const playersListOpen = playersListDiv && playersListDiv.style.display !== 'none';

    try {
      const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${editingPlayerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(player),
      });

      if (response.ok) {
        // Clear form and reset button
        firstNameInput.value = '';
        lastNameInput.value = '';
        ratingInput.value = '';
        if (titleInput) titleInput.value = '';
        if (federationInput) federationInput.value = '';
        if (clubInput) clubInput.value = '';
        if (fideIdInput) fideIdInput.value = '';
        if (federationCodeInput) federationCodeInput.value = '';
        if (birthDateInput) birthDateInput.value = '';
        if (genderSelect) genderSelect.value = '';
        addButton.textContent = t('player.add', 'Add');
        addButton.style.backgroundColor = '';
        delete addButton.dataset['editingPlayerId'];
        delete addButton.dataset['federation'];
        delete addButton.dataset['fideId'];
        delete addButton.dataset['federationCode'];

        // Remove cancel button
        const cancelButton = addButton.parentElement?.querySelector('.edit-cancel-btn');
        if (cancelButton) {
          cancelButton.remove();
        }

        // Reset form title back to "Add New Player"
        const playerForm = document.getElementById(`player-form-${tournamentId}`);
        const formTitle = playerForm?.querySelector('h4');
        if (formTitle) {
          formTitle.textContent = t('player.addNew', 'Add New Player');
        }

        // Save active tab before reload
        sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

        if (window.loadSingleTournament) {
          await window.loadSingleTournament(tournamentId);
        }
      } else {
        alert(t('player.updateError', 'Error updating player'));
      }
    } catch (error) {
      console.error('Error updating player:', error);
      alert(t('player.updateError', 'Error updating player'));
    }
    return;
  }

  // ADD new player
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(player),
    });

    if (response.ok) {
      const addedPlayer: Player = await response.json();

      // Save selected categories for the new player
      const categoryIds = window.getAddPlayerCategoryIds ? window.getAddPlayerCategoryIds(tournamentId) : [];
      if (categoryIds.length > 0) {
        try {
          await fetch(`${API_BASE_URL}/${tournamentId}/players/${addedPlayer.id}/categories`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ categoryIds }),
          });
        } catch (e) {
          console.warn('Could not save player categories:', e);
        }
      }

      // Clear the form inputs
      const searchInput = document.getElementById(`player-search-${tournamentId}`) as HTMLInputElement | null;
      if (searchInput) searchInput.value = '';
      firstNameInput.value = '';
      lastNameInput.value = '';
      ratingInput.value = '';
      if (titleInput) titleInput.value = '';
      if (federationInput) federationInput.value = '';
      if (clubInput) clubInput.value = '';
      if (fideIdInput) fideIdInput.value = '';
      if (federationCodeInput) federationCodeInput.value = '';
      if (birthDateInput) birthDateInput.value = '';
      if (genderSelect) genderSelect.value = '';
      delete addButton.dataset['federation'];
      delete addButton.dataset['fideId'];
      delete addButton.dataset['federationCode'];

      // Clear category selections
      if (window.clearAddPlayerCategories) {
        window.clearAddPlayerCategories(tournamentId);
      }

      // Save active tab before reload - always show players tab after adding a player
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');
      // Keep the add player form open after reload
      sessionStorage.setItem(`player-form-open-${tournamentId}`, 'true');

      // Reload single tournament
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }

      // Focus back on the first name input for quick entry and highlight the newly added player
      // Use a delay to ensure DOM is ready and event listeners are attached
      setTimeout(() => {
        // Restore form open state
        const playerForm = document.getElementById(`player-form-${tournamentId}`);
        const addPlayerBtn = document.getElementById(`add-player-btn-${tournamentId}`);
        if (playerForm && sessionStorage.getItem(`player-form-open-${tournamentId}`) === 'true') {
          playerForm.style.display = 'block';
          if (addPlayerBtn) addPlayerBtn.classList.add('active');
        }

        const firstNameInputAfterReload = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
        if (firstNameInputAfterReload) {
          firstNameInputAfterReload.focus();
        }
        highlightPlayer(tournamentId, addedPlayer.id);
      }, 100);
    } else if (response.status === 403) {
      // Check if it's a player limit error
      const errorData = await response.json();
      if (errorData.limit) {
        // Show upgrade prompt modal
        const title = t('player.limitReached', 'Player Limit Reached');
        const message = errorData.message || t('player.limitMessage', 'Free tier is limited to 20 players per tournament. Upgrade to Pro for unlimited players.');
        if (window.showUpgradePrompt) {
          window.showUpgradePrompt(title, message);
        } else {
          alert(message);
        }
      } else {
        alert(t('player.accessDenied', 'Access denied'));
      }
    } else {
      // Try to get specific error message from response
      try {
        const errorData = await response.json();
        if (errorData.error === 'errors.duplicatePlayer') {
          alert(t('player.duplicatePlayer', 'A player with this FIDE ID or Federation Code already exists in the tournament.'));
        } else {
          const errorMessage = errorData.error ? t(errorData.error, errorData.error) : t('player.addError', 'Error adding player');
          alert(errorMessage);
        }
      } catch {
        alert(t('player.addError', 'Error adding player'));
      }
    }
  } catch (error) {
    console.error('Error adding player:', error);
    alert(t('player.addError', 'Error adding player'));
  }
}

/**
 * Remove a player from a tournament
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function removePlayer(tournamentId: string, playerId: string): Promise<void> {
  try {
    // First, fetch tournament data to check if player has played any matches
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });

    if (!tournamentResponse.ok) {
      alert(t('error.loadTournament', 'Error loading tournament'));
      return;
    }

    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Check if player has played in any matches
    const playerMatches = tournament.matches.filter((m: any) =>
      (m.player1?.id === playerId || m.player2?.id === playerId)
    );

    if (playerMatches.length > 0) {
      // Player has played matches - show warning and prevent deletion
      const playerName = `${player.firstName} ${player.lastName}`.trim();
      const roundsPlayed = [...new Set(playerMatches.map((m: any) => m.round))].sort((a: number, b: number) => a - b);
      const roundsText = roundsPlayed.length === 1
        ? t('player.round', 'round') + ' ' + roundsPlayed[0]
        : t('player.rounds', 'rounds') + ' ' + roundsPlayed.join(', ');

      alert(t('player.cannotDeleteHasMatches', `Cannot delete ${playerName}. This player has already played in ${roundsText}. Delete the rounds first to remove this player.`));
      return;
    }

    // Show confirmation dialog for players without matches
    const confirmed = await showConfirmDialog(t('player.confirmRemove', 'Are you sure you want to remove this player?'));

    if (!confirmed) {
      return;
    }

    // Save UI state
    const playersListDiv = document.getElementById(`players-list-${tournamentId}`);
    const playersListOpen = playersListDiv && playersListDiv.style.display !== 'none';

    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.ok) {
      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } else {
      const errorData = await response.json();
      const errorMessage = errorData.error ? t(errorData.error, errorData.error) : t('player.removeError', 'Error removing player');
      alert(errorMessage);
    }
  } catch (error) {
    console.error('Error removing player:', error);
    alert(t('player.removeError', 'Error removing player'));
  }
}

/**
 * Load categories for a tournament
 */
async function loadCategoriesForPlayer(tournamentId: string): Promise<any[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/categories`, { credentials: 'include' });
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.warn('Could not fetch categories:', e);
  }
  return [];
}

/**
 * Edit a player in a tournament - creates inline edit form above the player row
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function editPlayer(tournamentId: string, playerId: string): Promise<void> {
  // Remove any existing inline edit forms
  const existingEditForm = document.querySelector('.inline-edit-form');
  if (existingEditForm) {
    existingEditForm.remove();
  }

  // Fetch tournament to get player data
  try {
    const [tournamentResponse, categories] = await Promise.all([
      fetch(`${API_BASE_URL}/${tournamentId}`, { credentials: 'include' }),
      loadCategoriesForPlayer(tournamentId)
    ]);
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Find the player row element
    const playerRow = document.getElementById(`player-${tournamentId}-${playerId}`);
    if (!playerRow) {
      alert(t('player.notFound', 'Player row not found'));
      return;
    }

    // Hide the main add player form if it's open
    const mainForm = document.getElementById(`player-form-${tournamentId}`);
    const addPlayerBtn = document.getElementById(`add-player-btn-${tournamentId}`);
    if (mainForm) {
      mainForm.style.display = 'none';
      if (addPlayerBtn) addPlayerBtn.classList.remove('active');
    }

    // Get form mode preference
    const savedFormMode = localStorage.getItem('playerFormMode') || 'simple';

    // Build categories checkboxes HTML
    const playerCategories = player.categories || [];
    const categoriesHtml = categories.length > 0 ? `
      <div class="player-categories-section player-field-advanced">
        <label class="categories-label">${t('player.categories', 'Categories')}:</label>
        <div class="player-categories-checkboxes">
          ${categories.map((cat: any) => `
            <label class="category-checkbox">
              <input type="checkbox" name="player-category" value="${cat.id}" ${playerCategories.includes(cat.id) ? 'checked' : ''}>
              <span>${escapeHtml(cat.name)}</span>
            </label>
          `).join('')}
        </div>
      </div>
    ` : '';

    // Create inline edit form
    const inlineForm = document.createElement('li');
    inlineForm.className = 'inline-edit-form player-form';
    inlineForm.dataset['mode'] = savedFormMode;
    inlineForm.innerHTML = `
      <h4>${t('player.edit', 'Edit Player')}</h4>
      <div class="player-input-group">
        <input type="text" id="inline-edit-first-name" placeholder="${t('tournament.firstName', 'First Name')}" required class="player-field-simple" value="${escapeHtml(player.firstName || '')}">
        <input type="text" id="inline-edit-last-name" placeholder="${t('tournament.lastName', 'Last Name')}" class="player-field-simple" value="${escapeHtml(player.lastName || '')}">
        <input type="number" id="inline-edit-rating" placeholder="${t('tournament.rating', 'Rating (optional)')}" class="player-field-simple" value="${player.rating || ''}">
        <select id="inline-edit-title" class="input-small player-field-advanced">
          <option value="">${t('tournament.noTitle', 'No Title')}</option>
          <option value="GM" ${player.title === 'GM' ? 'selected' : ''}>GM - ${t('tournament.grandmaster', 'Grandmaster')}</option>
          <option value="IM" ${player.title === 'IM' ? 'selected' : ''}>IM - ${t('tournament.internationalMaster', 'International Master')}</option>
          <option value="FM" ${player.title === 'FM' ? 'selected' : ''}>FM - ${t('tournament.fideMaster', 'FIDE Master')}</option>
          <option value="CM" ${player.title === 'CM' ? 'selected' : ''}>CM - ${t('tournament.candidateMaster', 'Candidate Master')}</option>
          <option value="WGM" ${player.title === 'WGM' ? 'selected' : ''}>WGM - ${t('tournament.womanGrandmaster', 'Woman Grandmaster')}</option>
          <option value="WIM" ${player.title === 'WIM' ? 'selected' : ''}>WIM - ${t('tournament.womanInternationalMaster', 'Woman International Master')}</option>
          <option value="WFM" ${player.title === 'WFM' ? 'selected' : ''}>WFM - ${t('tournament.womanFideMaster', 'Woman FIDE Master')}</option>
          <option value="WCM" ${player.title === 'WCM' ? 'selected' : ''}>WCM - ${t('tournament.womanCandidateMaster', 'Woman Candidate Master')}</option>
          <option value="NM" ${player.title === 'NM' ? 'selected' : ''}>NM - ${t('tournament.nationalMaster', 'National Master')}</option>
        </select>
        <input type="text" id="inline-edit-federation" placeholder="${t('tournament.federation', 'Federation (e.g., SUI)')}" class="input-small player-field-advanced" maxlength="3" style="text-transform: uppercase;" value="${escapeHtml(player.federation || '')}">
        <input type="text" id="inline-edit-club" placeholder="${t('tournament.club', 'Club (optional)')}" class="player-field-advanced" value="${escapeHtml(player.club || '')}">
        <input type="text" id="inline-edit-fide-id" placeholder="${t('tournament.fideId', 'FIDE ID (optional)')}" class="input-medium player-field-advanced" value="${escapeHtml(player.fideId || '')}">
        <button type="button" id="inline-edit-fetch-fide" class="btn btn-secondary btn-small player-field-advanced" style="${player.fideId ? '' : 'display: none;'}" title="${t('player.fetchFromFide', 'Fetch data from FIDE')}">🔄 ${t('player.fetchFide', 'Fetch FIDE')}</button>
        <input type="text" id="inline-edit-federation-code" placeholder="${t('tournament.federationCode', 'Federation Code (optional)')}" class="input-medium player-field-advanced" value="${escapeHtml(player.federationCode || '')}">
        <input type="date" id="inline-edit-birth-date" class="input-medium player-field-advanced" title="${t('player.birthDateHint', 'Birth date for age categories')}" value="${player.birthDate || ''}">
        <select id="inline-edit-gender" class="input-small player-field-advanced" title="${t('player.genderHint', 'Gender for category filtering')}">
          <option value="">${t('player.noGender', 'Gender')}</option>
          <option value="M" ${player.gender === 'M' ? 'selected' : ''}>${t('player.male', 'Male')}</option>
          <option value="F" ${player.gender === 'F' ? 'selected' : ''}>${t('player.female', 'Female')}</option>
        </select>
        <div class="fide-ratings-group player-field-advanced" style="display: flex; gap: 8px; width: 100%;">
          <input type="number" id="inline-edit-rating-std" placeholder="${t('player.ratingStd', 'Std')}" class="input-small" value="${player.ratingStd || ''}" title="${t('player.ratingStdTitle', 'FIDE Standard/Classical Rating')}">
          <input type="number" id="inline-edit-rating-rapid" placeholder="${t('player.ratingRapid', 'Rapid')}" class="input-small" value="${player.ratingRapid || ''}" title="${t('player.ratingRapidTitle', 'FIDE Rapid Rating')}">
          <input type="number" id="inline-edit-rating-blitz" placeholder="${t('player.ratingBlitz', 'Blitz')}" class="input-small" value="${player.ratingBlitz || ''}" title="${t('player.ratingBlitzTitle', 'FIDE Blitz Rating')}">
        </div>
        ${categoriesHtml}
        <button id="inline-edit-save" class="btn btn-primary" style="background-color: #f39c12;">${t('player.update', 'Update')}</button>
        <button id="inline-edit-cancel" class="btn btn-secondary">${t('common.cancel', 'Cancel')}</button>
      </div>
      <div class="player-form-footer">
        <a href="#" id="inline-edit-mode-toggle" class="player-mode-toggle">
          <span class="toggle-text-simple">${t('player.showAdvanced', 'Show more fields')}</span>
          <span class="toggle-text-advanced">${t('player.showSimple', 'Show fewer fields')}</span>
        </a>
      </div>
    `;

    // Insert the form above the player row
    playerRow.parentNode?.insertBefore(inlineForm, playerRow);

    // Scroll to form and focus
    inlineForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const firstNameInput = document.getElementById('inline-edit-first-name') as HTMLInputElement;
    if (firstNameInput) {
      setTimeout(() => firstNameInput.focus(), 100);
    }

    // Set up event handlers
    const saveButton = document.getElementById('inline-edit-save') as HTMLButtonElement;
    const cancelButton = document.getElementById('inline-edit-cancel') as HTMLButtonElement;
    const modeToggle = document.getElementById('inline-edit-mode-toggle') as HTMLAnchorElement;

    // Save handler
    saveButton?.addEventListener('click', async (e) => {
      e.preventDefault();
      await saveInlineEdit(tournamentId, playerId, inlineForm);
    });

    // Cancel handler
    cancelButton?.addEventListener('click', (e) => {
      e.preventDefault();
      inlineForm.remove();
    });

    // Mode toggle handler
    modeToggle?.addEventListener('click', (e) => {
      e.preventDefault();
      const currentMode = inlineForm.dataset['mode'] || 'simple';
      const newMode = currentMode === 'simple' ? 'advanced' : 'simple';
      inlineForm.dataset['mode'] = newMode;
      localStorage.setItem('playerFormMode', newMode);
    });

    // FIDE ID input change handler - show/hide fetch button
    const fideIdInput = document.getElementById('inline-edit-fide-id') as HTMLInputElement;
    const fetchFideButton = document.getElementById('inline-edit-fetch-fide') as HTMLButtonElement;
    fideIdInput?.addEventListener('input', () => {
      if (fetchFideButton) {
        fetchFideButton.style.display = fideIdInput.value.trim() ? '' : 'none';
      }
    });

    // Fetch from FIDE button handler
    fetchFideButton?.addEventListener('click', async (e) => {
      e.preventDefault();
      const fideId = fideIdInput?.value.trim();
      if (!fideId) return;

      fetchFideButton.disabled = true;
      fetchFideButton.textContent = '⏳ ...';

      try {
        const response = await fetch(`${API_BASE_URL}/fide-search?query=${encodeURIComponent(fideId)}`, {
          credentials: 'include',
        });
        const html = await response.text();

        // Parse FIDE results - reuse the parser from field-autocomplete
        const parser = new DOMParser();
        const doc = parser.parseFromString(html.replace(/<base[^>]*>/gi, ''), 'text/html');
        const rows = doc.querySelectorAll('tbody tr');

        for (const row of rows) {
          const cells = row.querySelectorAll('td');
          if (cells.length >= 9) {
            const rowFideId = cells[0]?.textContent?.trim();
            if (rowFideId === fideId) {
              // Found exact match
              const nameCell = cells[1];
              const link = nameCell?.querySelector('a');
              const fullName = link?.textContent?.trim() || '';
              const title = cells[2]?.textContent?.trim();
              const federation = cells[4]?.textContent?.trim();
              const ratingStd = parseInt(cells[5]?.textContent?.trim() || '') || undefined;
              const ratingRapid = parseInt(cells[6]?.textContent?.trim() || '') || undefined;
              const ratingBlitz = parseInt(cells[7]?.textContent?.trim() || '') || undefined;

              // Parse name
              let firstName = '', lastName = '';
              if (fullName.includes(',')) {
                const parts = fullName.split(',').map(p => p.trim());
                lastName = parts[0] || '';
                firstName = parts[1] || '';
              }

              // Populate fields
              const firstNameInput = document.getElementById('inline-edit-first-name') as HTMLInputElement;
              const lastNameInput = document.getElementById('inline-edit-last-name') as HTMLInputElement;
              const titleInput = document.getElementById('inline-edit-title') as HTMLSelectElement;
              const federationInput = document.getElementById('inline-edit-federation') as HTMLInputElement;
              const ratingStdInput = document.getElementById('inline-edit-rating-std') as HTMLInputElement;
              const ratingRapidInput = document.getElementById('inline-edit-rating-rapid') as HTMLInputElement;
              const ratingBlitzInput = document.getElementById('inline-edit-rating-blitz') as HTMLInputElement;
              const ratingInput = document.getElementById('inline-edit-rating') as HTMLInputElement;

              if (firstNameInput && firstName) firstNameInput.value = firstName;
              if (lastNameInput && lastName) lastNameInput.value = lastName;
              if (titleInput && title) titleInput.value = title;
              if (federationInput && federation) federationInput.value = federation.replace(/\s+/g, ' ').trim();
              if (ratingStdInput && ratingStd) ratingStdInput.value = String(ratingStd);
              if (ratingRapidInput && ratingRapid) ratingRapidInput.value = String(ratingRapid);
              if (ratingBlitzInput && ratingBlitz) ratingBlitzInput.value = String(ratingBlitz);
              // Also set main rating to std (or first available)
              if (ratingInput) ratingInput.value = String(ratingStd || ratingRapid || ratingBlitz || '');

              break;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching from FIDE:', error);
        alert(t('player.fideError', 'Error fetching data from FIDE'));
      } finally {
        fetchFideButton.disabled = false;
        fetchFideButton.textContent = `🔄 ${t('player.fetchFide', 'Fetch FIDE')}`;
      }
    });

    // Enter key handler for all inputs
    const inputs = inlineForm.querySelectorAll('input');
    inputs.forEach(input => {
      input.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          await saveInlineEdit(tournamentId, playerId, inlineForm);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          inlineForm.remove();
        }
      });
    });

  } catch (error) {
    console.error('Error fetching player data:', error);
    alert(t('player.loadError', 'Error loading player data'));
  }
}

/**
 * Helper function to escape HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Save inline edit form data
 */
async function saveInlineEdit(tournamentId: string, playerId: string, formElement: HTMLElement): Promise<void> {
  const firstNameInput = document.getElementById('inline-edit-first-name') as HTMLInputElement;
  const lastNameInput = document.getElementById('inline-edit-last-name') as HTMLInputElement;
  const ratingInput = document.getElementById('inline-edit-rating') as HTMLInputElement;
  const ratingStdInput = document.getElementById('inline-edit-rating-std') as HTMLInputElement;
  const ratingRapidInput = document.getElementById('inline-edit-rating-rapid') as HTMLInputElement;
  const ratingBlitzInput = document.getElementById('inline-edit-rating-blitz') as HTMLInputElement;
  const titleInput = document.getElementById('inline-edit-title') as HTMLSelectElement;
  const federationInput = document.getElementById('inline-edit-federation') as HTMLInputElement;
  const clubInput = document.getElementById('inline-edit-club') as HTMLInputElement;
  const fideIdInput = document.getElementById('inline-edit-fide-id') as HTMLInputElement;
  const federationCodeInput = document.getElementById('inline-edit-federation-code') as HTMLInputElement;
  const birthDateInput = document.getElementById('inline-edit-birth-date') as HTMLInputElement;
  const genderSelect = document.getElementById('inline-edit-gender') as HTMLSelectElement;

  const firstName = firstNameInput?.value.trim();
  if (!firstName) {
    alert(t('player.enterFirstName', 'Please enter a first name'));
    return;
  }

  const playerData: PlayerData = {
    firstName,
    lastName: lastNameInput?.value.trim() || undefined,
    rating: parseInt(ratingInput?.value) || undefined,
    ratingStd: parseInt(ratingStdInput?.value) || undefined,
    ratingRapid: parseInt(ratingRapidInput?.value) || undefined,
    ratingBlitz: parseInt(ratingBlitzInput?.value) || undefined,
    title: titleInput?.value || undefined,
    club: clubInput?.value.trim() || undefined,
    federation: federationInput?.value.trim().toUpperCase() || undefined,
    fideId: fideIdInput?.value.trim() || undefined,
    federationCode: federationCodeInput?.value.trim() || undefined,
    birthDate: birthDateInput?.value || undefined,
    gender: (genderSelect?.value as 'M' | 'F') || undefined
  };

  // Collect selected category IDs
  const categoryCheckboxes = formElement.querySelectorAll('input[name="player-category"]:checked') as NodeListOf<HTMLInputElement>;
  const selectedCategories = Array.from(categoryCheckboxes).map(cb => cb.value);

  try {
    // Update player data
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(playerData),
    });

    if (response.ok) {
      // Also update player categories if there are category checkboxes
      if (formElement.querySelector('.player-categories-section')) {
        try {
          await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}/categories`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ categoryIds: selectedCategories }),
          });
        } catch (e) {
          console.warn('Could not update player categories:', e);
        }
      }

      // Remove the inline form
      formElement.remove();

      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }

      // Highlight the updated player
      setTimeout(() => {
        highlightPlayer(tournamentId, playerId);
      }, 100);
    } else {
      const errorData = await response.json();
      const errorMessage = errorData.error ? t(errorData.error, errorData.error) : t('player.updateError', 'Error updating player');
      alert(errorMessage);
    }
  } catch (error) {
    console.error('Error updating player:', error);
    alert(t('player.updateError', 'Error updating player'));
  }
}

/**
 * Highlight a player in the UI
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export function highlightPlayer(tournamentId: string, playerId: string): void {
  const playerElement = document.getElementById(`player-${tournamentId}-${playerId}`);

  if (playerElement) {
    // Add highlight class
    playerElement.classList.add('player-highlight');

    // Scroll to the player
    playerElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Remove highlight after 2 seconds
    setTimeout(() => {
      playerElement.classList.remove('player-highlight');
    }, 2000);
  } else {
    console.warn(`Player element not found: player-${tournamentId}-${playerId}`);
  }
}

/**
 * Toggle the add player form visibility
 * @param tournamentId - The tournament ID
 */
export function toggleAddPlayerForm(tournamentId: string): void {
  const playerForm = document.getElementById(`player-form-${tournamentId}`);
  const addButton = document.getElementById(`add-player-btn-${tournamentId}`);

  if (!playerForm) {
    console.warn(`Player form not found: player-form-${tournamentId}`);
    return;
  }

  const isHidden = playerForm.style.display === 'none' || playerForm.style.display === '';
  playerForm.style.display = isHidden ? 'block' : 'none';

  // Update button state
  if (addButton) {
    addButton.classList.toggle('active', isHidden);
  }

  // Focus search input when opening
  if (isHidden) {
    const searchInput = document.getElementById(`player-search-${tournamentId}`) as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
    }
  }
}

/**
 * Toggle player form between simple and advanced mode
 * @param tournamentId - The tournament ID
 */
export function togglePlayerFormMode(tournamentId: string): void {
  const playerForm = document.getElementById(`player-form-${tournamentId}`);

  if (!playerForm) {
    console.warn(`Player form not found: player-form-${tournamentId}`);
    return;
  }

  const currentMode = playerForm.dataset['mode'] || 'simple';
  const newMode = currentMode === 'simple' ? 'advanced' : 'simple';

  playerForm.dataset['mode'] = newMode;

  // Store preference in localStorage
  try {
    localStorage.setItem('playerFormMode', newMode);
  } catch (error) {
    console.warn('Could not save player form mode preference:', error);
  }
}

/**
 * Toggle whether a player can enter their own results
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 * @param currentValue - Current value of canEnterResults
 */
export async function togglePlayerCanEnterResults(tournamentId: string, playerId: string, currentValue: boolean): Promise<void> {
  const newValue = !currentValue;

  try {
    // Fetch current player data
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Update the player with the new canEnterResults value
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...player,
        canEnterResults: newValue
      }),
    });

    if (response.ok) {
      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

      // Reload single tournament to reflect changes
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } else {
      alert(t('player.updateError', 'Error updating player'));
    }
  } catch (error) {
    console.error('Error toggling player result entry:', error);
    alert(t('player.updateError', 'Error updating player'));
  }
}

/**
 * Toggle whether a player is marked as present (checked in)
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 * @param isPresent - New present value
 */
export async function togglePlayerPresent(tournamentId: string, playerId: string, isPresent: boolean): Promise<void> {
  try {
    // Fetch current player data
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Update the player with the new present value
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...player,
        present: isPresent
      }),
    });

    if (response.ok) {
      // Update the data-present attribute for filtering
      const playerRow = document.getElementById(`player-${tournamentId}-${playerId}`);
      if (playerRow) {
        playerRow.dataset.present = isPresent ? 'true' : 'false';
      }
      // Re-apply present filter if active
      const presentFilter = document.getElementById(`player-present-filter-${tournamentId}`) as HTMLSelectElement;
      if (presentFilter && presentFilter.value !== 'all') {
        window.filterPlayersByPresent?.(tournamentId, presentFilter.value);
      }
    } else {
      alert(t('player.updateError', 'Error updating player'));
      // Revert checkbox state
      const checkbox = document.querySelector(`#player-${tournamentId}-${playerId} .present-checkbox input`) as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = !isPresent;
      }
    }
  } catch (error) {
    console.error('Error toggling player present:', error);
    alert(t('player.updateError', 'Error updating player'));
    // Revert checkbox state
    const checkbox = document.querySelector(`#player-${tournamentId}-${playerId} .present-checkbox input`) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !isPresent;
    }
  }
}

/**
 * Open the payment modal to mark a player's entry fee as paid
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function promptPlayerFeePaid(tournamentId: string, playerId: string): Promise<void> {
  try {
    // Fetch current player data to show name
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    const playerName = `${player.firstName} ${player.lastName || ''}`.trim();

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.id = `payment-modal-${tournamentId}-${playerId}`;
    modal.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 99999; opacity: 1;';

    modal.innerHTML = `
      <div class="modal-content payment-modal" style="background: white; border-radius: 12px; max-width: 400px; width: 90%; box-shadow: 0 8px 32px rgba(0,0,0,0.2);">
        <div class="modal-header" style="display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb;">
          <h3 style="margin: 0; font-size: 1.25rem;">💰 ${t('player.markAsPaid', 'Mark Entry Fee as Paid')}</h3>
          <button class="modal-close" onclick="closePaymentModal('${tournamentId}', '${playerId}')" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280;">&times;</button>
        </div>
        <div class="modal-body" style="padding: 20px;">
          <p style="font-weight: 600; margin-bottom: 16px; color: #333;">
            ${escapeHtml(playerName)}
          </p>
          <div style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; margin-bottom: 6px; font-weight: 500; color: #374151;">
                ${t('player.paymentAmount', 'Amount')}
              </label>
              <input
                type="number"
                id="payment-amount-${playerId}"
                step="0.01"
                min="0"
                placeholder="20.00"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 16px; box-sizing: border-box;"
                autofocus
              >
            </div>
            <div>
              <label style="display: block; margin-bottom: 6px; font-weight: 500; color: #374151;">
                ${t('player.paymentCurrency', 'Currency')}
              </label>
              <select
                id="payment-currency-${playerId}"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 16px; background: white; cursor: pointer; box-sizing: border-box;">
                <!-- Europe -->
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CHF">CHF - Swiss Franc</option>
                <option value="PLN">PLN - Polish Zloty</option>
                <option value="CZK">CZK - Czech Koruna</option>
                <option value="SEK">SEK - Swedish Krona</option>
                <option value="NOK">NOK - Norwegian Krone</option>
                <option value="DKK">DKK - Danish Krone</option>
                <option value="HUF">HUF - Hungarian Forint</option>
                <option value="RON">RON - Romanian Leu</option>
                <option value="BGN">BGN - Bulgarian Lev</option>
                <option value="RSD">RSD - Serbian Dinar</option>
                <option value="UAH">UAH - Ukrainian Hryvnia</option>
                <option value="RUB">RUB - Russian Ruble</option>
                <option value="TRY">TRY - Turkish Lira</option>
                <!-- Americas -->
                <option value="USD">USD - US Dollar</option>
                <option value="BRL">BRL - Brazilian Real</option>
                <option value="MXN">MXN - Mexican Peso</option>
                <option value="ARS">ARS - Argentine Peso</option>
                <!-- Asia -->
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="CNY">CNY - Chinese Yuan</option>
                <option value="KRW">KRW - Korean Won</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="IDR">IDR - Indonesian Rupiah</option>
                <option value="BDT">BDT - Bangladeshi Taka</option>
                <!-- Middle East -->
                <option value="SAR">SAR - Saudi Riyal</option>
                <option value="AED">AED - UAE Dirham</option>
                <option value="EGP">EGP - Egyptian Pound</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer" style="display: flex; justify-content: flex-end; gap: 12px; padding: 16px 20px; border-top: 1px solid #e5e7eb;">
          <button onclick="closePaymentModal('${tournamentId}', '${playerId}')" style="padding: 10px 20px; border: 1px solid #d1d5db; background: white; border-radius: 6px; cursor: pointer; font-size: 14px;">
            ${t('common.cancel', 'Cancel')}
          </button>
          <button onclick="savePlayerPayment('${tournamentId}', '${playerId}')" style="padding: 10px 20px; border: none; background: #10b981; color: white; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;">
            ${t('player.confirmPayment', 'Confirm Payment')}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Add keyboard listeners
    const amountInput = document.getElementById(`payment-amount-${playerId}`) as HTMLInputElement;
    if (amountInput) {
      setTimeout(() => amountInput.focus(), 50);

      amountInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          savePlayerPayment(tournamentId, playerId);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closePaymentModal(tournamentId, playerId);
        }
      });
    }

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closePaymentModal(tournamentId, playerId);
      }
    });

  } catch (error) {
    console.error('Error opening payment modal:', error);
    alert(t('error.generic', 'An error occurred'));
  }
}

/**
 * Close the payment modal
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export function closePaymentModal(tournamentId: string, playerId: string): void {
  const modal = document.getElementById(`payment-modal-${tournamentId}-${playerId}`);
  if (modal) {
    modal.remove();
  }
}

/**
 * Save the player payment from the modal
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function savePlayerPayment(tournamentId: string, playerId: string): Promise<void> {
  const amountInput = document.getElementById(`payment-amount-${playerId}`) as HTMLInputElement;
  const currencySelect = document.getElementById(`payment-currency-${playerId}`) as HTMLSelectElement;

  if (!amountInput || !currencySelect) {
    return;
  }

  const amount = parseFloat(amountInput.value);
  if (isNaN(amount) || amount < 0) {
    amountInput.style.borderColor = '#ef4444';
    amountInput.focus();
    return;
  }

  // Convert to cents
  const amountInCents = Math.round(amount * 100);
  const currency = currencySelect.value;

  try {
    // Fetch current player data
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      closePaymentModal(tournamentId, playerId);
      return;
    }

    // Update the player with the payment info
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...player,
        feePaid: true,
        paidAmount: amountInCents,
        paidCurrency: currency,
        paymentSource: 'manual'
      }),
    });

    if (response.ok) {
      // Close the modal
      closePaymentModal(tournamentId, playerId);

      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

      // Reload to update the paid badge display
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } else {
      alert(t('player.updateError', 'Error updating player'));
    }
  } catch (error) {
    console.error('Error marking player fee paid:', error);
    alert(t('player.updateError', 'Error updating player'));
  }
}

/**
 * Clear a player's manual payment status
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function clearPlayerFeePaid(tournamentId: string, playerId: string): Promise<void> {
  try {
    // Fetch current player data
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    const tournament = await tournamentResponse.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Update the player to clear payment info
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...player,
        feePaid: false,
        paidAmount: undefined,
        paidCurrency: undefined
      }),
    });

    if (response.ok) {
      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

      // Reload to update the display
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } else {
      alert(t('player.updateError', 'Error updating player'));
    }
  } catch (error) {
    console.error('Error clearing player fee paid:', error);
    alert(t('player.updateError', 'Error updating player'));
  }
}

// Export to window for onclick handlers

window.addPlayer = addPlayer;
window.removePlayer = removePlayer;
window.editPlayer = editPlayer;
window.highlightPlayer = highlightPlayer;
window.toggleAddPlayerForm = toggleAddPlayerForm;
window.togglePlayerFormMode = togglePlayerFormMode;
window.togglePlayerCanEnterResults = togglePlayerCanEnterResults;
window.togglePlayerPresent = togglePlayerPresent;
window.promptPlayerFeePaid = promptPlayerFeePaid;
window.closePaymentModal = closePaymentModal;
window.savePlayerPayment = savePlayerPayment;
window.clearPlayerFeePaid = clearPlayerFeePaid;
window.setupPlayerFormKeyHandlers = setupPlayerFormKeyHandlers;
